import { useMutation } from "@tanstack/react-query";
import { useLocation, useNavigate } from "@tanstack/react-router";
import { Navbar as BNavbar, Container } from "react-bootstrap";
import { List } from "react-bootstrap-icons";
import { useTranslation } from "react-i18next";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/hooks/useAuth";
import useConfig from "@/hooks/useConfig";
import logo from "@/images/logo-wordmark-reverse.svg";
import { EMAIL_KEY, FIRM_CODE_KEY, FIRM_NAME_KEY } from "@/lib/constants";
import { safeJsonParse } from "@/lib/utils";
import styles from "./Navbar.module.scss";

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { i18n, t } = useTranslation();
  const isVerificationPage = location.pathname === "/";
  const { isAuthenticated, setIsAuthenticated } = useAuth();
  const { config } = useConfig();
  const apiUrl = config?.ApiUrl ?? import.meta.env.VITE_API_URL;

  const logoutMutation = useMutation({
    mutationFn: async () => {
      await fetch(`${apiUrl}/Account/logout`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });
    },
    onSuccess: () => {
      localStorage.removeItem(FIRM_NAME_KEY);
      localStorage.removeItem(FIRM_CODE_KEY);
      localStorage.removeItem(EMAIL_KEY);
      setIsAuthenticated(false);
      void navigate({ to: "/" });
    },
  });

  // Remove the isEngToggled state and use i18n.language directly
  const toggleLanguage = () => {
    const newLang = i18n.language === "en" ? "es" : "en";
    void i18n.changeLanguage(newLang);
  };

  const firmName =
    safeJsonParse<string>(localStorage.getItem(FIRM_NAME_KEY) || "") || "";

  return (
    <BNavbar
      className={`${isVerificationPage && styles.homePage}`}
      expand="sm"
      bg="dark"
      variant="dark"
    >
      <Container fluid>
        <div className={styles.navbarContainer}>
          {/* Desktop layout */}
          <div className={styles.leftSide}>
            <span>{firmName}</span>
          </div>
          <div className={styles.rightSide}>
            <div className={styles.navLinks}>
              <input
                type="button"
                className={styles.languageButton}
                onClick={() => toggleLanguage()}
                value={i18n.language === "en" ? "Español" : "English"}
              />
            </div>
            {isAuthenticated && (
              <input
                type="button"
                className={styles.logoutButton}
                onClick={() => logoutMutation.mutate()}
                onKeyDown={(e) => e.key === "Enter" && logoutMutation.mutate()}
                tabIndex={0}
                value={t("formLabels.labelLogout")}
                disabled={logoutMutation.isPending}
              />
            )}
            <div className={styles.securedBy}>
              <BNavbar.Brand href="/">
                {t("verification.navBarSecuredBy")}
                <img src={logo} alt="logo" className="logo" />
              </BNavbar.Brand>
            </div>
          </div>

          {/* Mobile layout */}
          <div className={styles.mobileLeft}>
            <span className={styles.mobileFirmName}>{firmName}</span>
            <a href="/" className={styles.mobileSecuredBy}>
              {t("verification.navBarSecuredBy")}
              <img src={logo} alt="logo" />
            </a>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className={styles.hamburger}
                aria-label="Menu"
              >
                <List size={24} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={() => toggleLanguage()}>
                {i18n.language === "en" ? "Español" : "English"}
              </DropdownMenuItem>
              {isAuthenticated && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onSelect={() => logoutMutation.mutate()}
                    disabled={logoutMutation.isPending}
                  >
                    {t("formLabels.labelLogout")}
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </Container>
    </BNavbar>
  );
}
